import React, { useEffect, useRef, useState } from 'react';
import { searchCities } from '../lib/state';
import { formatOffset, offsetMinutes } from '../lib/time';

/** Combobox: type, arrow through matches, Enter or click to add. */
export default function CitySearch({ onAdd, exclude }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const results = searchCities(q, exclude);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    const close = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  const add = (city) => {
    if (!city) return;
    onAdd(city);
    setQ('');
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); add(results[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="search" ref={box}>
      <input
        type="search"
        value={q}
        placeholder="Add a city or time zone"
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls="city-results"
        aria-activedescendant={results[active] ? `city-opt-${active}` : undefined}
        aria-label="Add a city"
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <ul className="results" id="city-results" role="listbox">
          {results.map((c, i) => (
            <li
              key={c.name}
              id={`city-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'active' : ''}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); add(c); }}
            >
              <span className="r-name">{c.name}{c.country ? <span className="r-cc">{c.country}</span> : null}</span>
              <span className="r-zone">{formatOffset(offsetMinutes(c.timezone, Date.now()))}</span>
            </li>
          ))}
        </ul>
      )}
      {open && q.trim() && results.length === 0 && <div className="results empty-results">No city matches “{q}”</div>}
    </div>
  );
}
